import {
  type ComponentPropsWithoutRef,
  type ComponentPropsWithRef,
  type ElementType,
  type JSX,
} from 'react';

/* eslint-disable-next-line no-restricted-imports */
import RootIconButton, {
  type IconButtonTypeMap as RootIconButtonTypeMap,
} from '@mui/material/IconButton';
import { type BaseProps, type OverridableTypeMap } from '@mui/material/OverridableComponent';

import { type TitleCase } from '@craft/lib/formatters';

import { type QuantitativeSizeString } from '../../types/sizes';
import { interactableStyle } from '../../util/interaction/parsing';
import { Interaction, InteractionIndicator } from '../../util/interaction/types';
import { mergeSx } from '../../util/mui/sx';
import { quantitativeSizeToString } from '../../util/sizes';
import { Icon } from '../icons/Icon';
import { type IconProp, type IconProps, type IconSize } from '../icons/types';

import { DefaultIconButtonPadding } from './constants';
import { parseButtonHeight, parseIconButtonPadding } from './style';
import { ButtonDefaultSize, ButtonDiscreteSize, type ButtonSize } from './types';

const MinimumIconSizeAdjustment = 4;
const ConstantMinimumIconSize = 12;

/**
 * Returns the minimum icon size that should be applied to the {@link Icon} that is rendered inside
 * of the {@link IconButton} based on the provided size of the {@link IconButton} and the
 * padding that is applied inside of the {@link IconButton}.
 *
 * The {@link Icon} inside of the {@link IconButton} is rendered with a height of 100%, meaning
 * that the {@link Icon}'s size will be the size of the {@link IconButton} minus the padding that is
 * applied to the {@link IconButton}:
 *
 * 1. Size = Large, Height = 40px, Padding = 6px => Icon Size = 40px - 2*6px = 28px
 * 2. Size = Medium, Height = 36px, Padding = 6px => Icon Size = 36px - 2*6px = 24px
 * 3. Size = Small, Height = 32px, Padding = 6px => Icon Size = 32px - 2*6px = 20px
 *
 * However, in cases where the maximum size/height of the {@link IconButton} is responsive,
 * and based on 100% of the parent container's height, the actual rendered size of the
 * {@link IconButton} may be smaller than the defined height for the provided size.  Because the
 * padding remains constant, this means that the actual size of the {@link Icon} may be smaller
 * than what is expected based on the prop-provided size.
 *
 * This function defines the minimum allowed size of the {@link Icon}, depending on the size
 * of the {@link IconButton}, to prevent the {@link Icon} from being rendered too small when
 * the {@link IconButton} is constrained within the height of the parent container.
 *
 * @param {ButtonSize} size
 *   The prop provided size of the {@link IconButton}.
 *
 * @returns {QuantitativeSizeString<'px'> | undefined}
 *   The minimum allowed size of the {@link Icon} inside of the {@link IconButton}, in pixels,
 *   or undefined in the case where the size of the {@link IconButton} is "auto".
 */
export const getMinIconSize = (size: ButtonSize): QuantitativeSizeString<'px'> | undefined => {
  if (size === 'auto') {
    return undefined;
  }
  const padding = parseIconButtonPadding(size);
  const height = parseButtonHeight(size, { as: 'number' });
  if (height === null) {
    return undefined;
  }
  return quantitativeSizeToString(
    Math.max(ConstantMinimumIconSize, height - 2 * padding - MinimumIconSizeAdjustment),
    'px',
  );
};

type DefaultOmitableIconButtonProp =
  | 'disabled'
  | 'disableFocusRipple'
  | 'disableRipple'
  | 'disableTouchRipple'
  | 'size';

type OmitableIconButtonProp<E extends ElementType, T extends OverridableTypeMap> =
  | Exclude<keyof BaseProps<T>, DefaultOmitableIconButtonProp>
  | keyof ComponentPropsWithoutRef<E>;

type IconButtonTypeMap<E extends ElementType> = RootIconButtonTypeMap<{ component?: E }, E>;

export type IconButtonDefaultComponent = RootIconButtonTypeMap['defaultComponent'];

/**
 * Represents the root MUI props for the {@link IconButton} component.
 *
 * This type must be generated by distributing over the union {@link T}, which is a type map that
 * already distributes over the union {@link E}.
 *
 * This type is analogous to Material UI's {@link OverrideProps} type, with the one exception being
 * the omission of the types from the {@link BaseProps<T>} shown below.  This omission has to be
 * performed on the {@link BaseProps<T>}, instead of on the overall resulting type
 * {@link RootIconButtonProps}, due to Material UI internal type definitions related to the types
 * used below.
 *
 * @see https://mui.com/material-ui/guides/composition/#generic for more information related to the
 * extension of generically typed Material UI components.
 */
type RootIconButtonProps<
  E extends ElementType,
  T extends OverridableTypeMap,
  O extends OmitableIconButtonProp<E, T> = never,
> = T extends OverridableTypeMap
  ? Omit<BaseProps<T>, DefaultOmitableIconButtonProp | O> &
      Omit<ComponentPropsWithoutRef<E>, keyof BaseProps<T> | O>
  : never;

export type IconButtonPartialProps = {
  readonly icon?: IconProp;
  readonly iconProps?: Omit<IconProps, 'size'>;
  readonly iconSize?: IconSize;
  readonly isDisabled?: boolean;
  readonly isLoading?: boolean;
  readonly isLocked?: boolean;
  readonly size?: ButtonSize;
};

/**
 * Represents the props for the {@link IconButton} component, which are a combination of internally
 * defined props and props that are extended directly from MUI, {@link RootIconButtonProps}.
 */
export type IconButtonProps<
  E extends ElementType = IconButtonDefaultComponent,
  P = IconButtonPartialProps,
  O extends OmitableIconButtonProp<E, IconButtonTypeMap<E>> = never,
> = P & RootIconButtonProps<E, IconButtonTypeMap<E>, O>;

const IconButtonBoxProperties = {
  alignItems: 'center',
  aspectRatio: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: 'auto',
};

type IconButtonComponentType = <E extends ElementType = IconButtonDefaultComponent>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & IconButtonProps<E>,
) => JSX.Element;

type IconButtonPartialComponentType = <E extends ElementType = IconButtonDefaultComponent>(
  props: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & IconButtonProps<
    E,
    Omit<IconButtonPartialProps, 'size'>
  >,
) => JSX.Element;

export const LocalIconButton: IconButtonComponentType &
  Partial<Record<TitleCase<ButtonDiscreteSize>, IconButtonPartialComponentType>> = <
  E extends ElementType = IconButtonDefaultComponent,
>({
  children,
  icon,
  iconProps,
  iconSize,
  isDisabled,
  isLoading,
  isLocked,
  ref,
  size = ButtonDefaultSize,
  ...props
}: { readonly ref?: ComponentPropsWithRef<E>['ref'] } & IconButtonProps<E>): JSX.Element => (
  <RootIconButton
    {...props}
    disabled={isDisabled}
    disableFocusRipple
    disableRipple
    disableTouchRipple
    ref={ref}
    sx={mergeSx(
      {
        height: parseButtonHeight(size, { as: 'string' }),
        padding:
          size === 'auto'
            ? quantitativeSizeToString(DefaultIconButtonPadding, 'px')
            : quantitativeSizeToString(parseIconButtonPadding(size), 'px'),
      },
      t =>
        interactableStyle(
          t,
          [
            {
              indicator: InteractionIndicator.Background,
              interactions: [Interaction.FocusVisible, Interaction.Focus],
              overrides: { backgroundColor: 'action.focus' },
            },
            {
              indicator: InteractionIndicator.Background,
              interactions: [Interaction.Hover],
              overrides: { backgroundColor: 'action.hover' },
            },
          ],
          {
            selectors: {
              [Interaction.Hover]: '&:hover:not(:focus):not(:focus-visible):not(.Mui-focusVisible)',
            },
          },
        ),
      props.sx,
      {
        '> .MuiSvgIcon-root': {
          ...IconButtonBoxProperties,
          height: '100%',
        },
        ...IconButtonBoxProperties,
        flex: 'none',
        overflow: 'hidden',
        pointerEvents: isLoading || isDisabled || isLocked ? 'none' : 'auto',
        whiteSpace: 'nowrap',
      },
    )}
  >
    {icon || isLoading ? (
      <Icon
        {...iconProps}
        icon={icon}
        isLoading={isLoading}
        size={iconSize}
        style={{
          maxHeight: '100%',
          minHeight: getMinIconSize(size),
          minWidth: getMinIconSize(size),
          ...iconProps?.style,
        }}
      />
    ) : (
      children
    )}
  </RootIconButton>
);

const createIconButtonPartial = (size: ButtonDiscreteSize): IconButtonPartialComponentType => {
  const IconButtonPartial: IconButtonPartialComponentType = <
    E extends ElementType = IconButtonDefaultComponent,
  >({
    ref,
    ...props
  }: {
    readonly ref?: ComponentPropsWithRef<E>['ref'];
  } & IconButtonProps<E, Omit<IconButtonPartialProps, 'size'>>) => (
    <LocalIconButton
      {...(props as IconButtonProps<E, Omit<IconButtonPartialProps, 'size'>>)}
      ref={ref}
      size={size}
    />
  );
  return IconButtonPartial;
};

LocalIconButton.Xsmall = createIconButtonPartial(ButtonDiscreteSize.XSmall);
LocalIconButton.Small = createIconButtonPartial(ButtonDiscreteSize.Small);
LocalIconButton.Medium = createIconButtonPartial(ButtonDiscreteSize.Medium);
LocalIconButton.Large = createIconButtonPartial(ButtonDiscreteSize.Large);

export const IconButton = LocalIconButton as IconButtonComponentType &
  Record<TitleCase<ButtonDiscreteSize>, IconButtonPartialComponentType>;
